import { useMutation } from '@tanstack/react-query'
import { createApiCall } from 'services'

export type ChangePasswordPayload = {
  currentPassword?: string
  newPassword: string
  confirmPassword: string
}

export type ChangePasswordResponse = {
  success: boolean
  message: string
}

type ChangePasswordApiError = Error & {
  status?: number
}

const toMutationError = (status: number, fallbackMessage: string, message?: string): ChangePasswordApiError => {
  const error = new Error(message || fallbackMessage) as ChangePasswordApiError
  error.status = status
  return error
}

export const useChangePasswordMutation = () => {
  const mutation = useMutation<ChangePasswordResponse, ChangePasswordApiError, ChangePasswordPayload>({
    mutationFn: async payload => {
      const response = await createApiCall({
        method: 'PUT',
        url: '/auth/admin/password',
        data: payload,
      })

      if (response.status !== 200) {
        throw toMutationError(
          response.status,
          'Failed to change password',
          typeof response.data?.message === 'string' ? response.data.message : undefined
        )
      }

      return response.data as ChangePasswordResponse
    },
  })

  return {
    changePassword: mutation.mutate,
    changePasswordAsync: mutation.mutateAsync,
    isChangingPassword: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  }
}
